import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Check, X } from 'lucide-react';
import { familyStorage, FamilyRelationship, FamilyMember } from '@/lib/familyStorage';
import { useBrand } from '@/config/brands';
import { toast } from 'sonner';

interface AddMemberFormProps {
  onAdded: (member: FamilyMember) => void;
  onCancel?: () => void;
  editMember?: FamilyMember;
  compact?: boolean;
}

const RELATIONSHIP_KEYS: { value: FamilyRelationship; i18nKey: string; fallback: string }[] = [
  { value: 'self', i18nKey: 'memberTypes.self', fallback: 'Self (Me)' },
  { value: 'spouse', i18nKey: 'memberTypes.spouse', fallback: 'Spouse/Partner' },
  { value: 'child', i18nKey: 'memberTypes.child', fallback: 'Child' },
  { value: 'parent', i18nKey: 'memberTypes.parent', fallback: 'Parent' },
  { value: 'sibling', i18nKey: 'memberTypes.sibling', fallback: 'Sibling' },
  { value: 'other', i18nKey: 'memberTypes.other', fallback: 'Other' },
];

export function AddMemberForm({ onAdded, onCancel, editMember, compact }: AddMemberFormProps) {
  const { t } = useTranslation('family');
  const brand = useBrand();
  const isAhp = brand.design === 'civic';

  const [name, setName] = useState(editMember?.name ?? '');
  const [relationship, setRelationship] = useState<FamilyRelationship>(editMember?.relationship ?? 'spouse');

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error(t('addMember.nameRequired', 'Please enter a name'));
      return;
    }

    if (editMember) {
      const updated = familyStorage.updateMember(editMember.id, { name: trimmed, relationship });
      if (updated) {
        onAdded(updated);
        toast.success(t('addMember.memberUpdated', 'Member updated'));
      }
    } else {
      const member = familyStorage.addMember(trimmed, relationship);
      onAdded(member);
      toast.success(t('addMember.memberAdded', { name: trimmed, defaultValue: `Added ${trimmed} to your family` }));
    }

    setName('');
    setRelationship('spouse');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape' && onCancel) {
      onCancel();
    }
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <Input
          placeholder={t('addMember.namePlaceholder')}
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={handleKeyDown}
          className="h-8 text-xs w-28"
          autoFocus
        />
        <Select value={relationship} onValueChange={v => setRelationship(v as FamilyRelationship)}>
          <SelectTrigger className="h-8 text-xs w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RELATIONSHIP_KEYS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{t(opt.i18nKey, opt.fallback)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" onClick={handleSave} className={`h-8 px-2 ${isAhp ? 'bg-civic-red hover:bg-[#9a1d2d] text-white' : 'bg-matrix-green text-black hover:bg-matrix-green/90'}`}>
          <Check className="w-3 h-3" />
        </Button>
        {onCancel && (
          <Button size="sm" variant="ghost" onClick={onCancel} className="h-8 px-2">
            <X className="w-3 h-3" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-end gap-3">
      <div className="flex-1 space-y-1">
        <label className="text-xs font-medium text-muted-foreground">{t('addMember.name')}</label>
        <Input
          placeholder={t('addMember.namePlaceholder')}
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={handleKeyDown}
          className="h-9"
          autoFocus
        />
      </div>
      <div className="w-40 space-y-1">
        <label className="text-xs font-medium text-muted-foreground">{t('addMember.relationship')}</label>
        <Select value={relationship} onValueChange={v => setRelationship(v as FamilyRelationship)}>
          <SelectTrigger className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RELATIONSHIP_KEYS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{t(opt.i18nKey, opt.fallback)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button
        onClick={handleSave}
        size="sm"
        className={`h-9 ${isAhp ? 'bg-civic-red hover:bg-[#9a1d2d] text-white' : 'bg-matrix-green text-black hover:bg-matrix-green/90'}`}
      >
        <Plus className="w-4 h-4 mr-1" />
        {editMember ? t('addMember.update', 'Update') : t('addMember.save', 'Add')}
      </Button>
      {onCancel && (
        <Button variant="ghost" size="sm" onClick={onCancel} className="h-9">
          {t('addMember.cancel')}
        </Button>
      )}
    </div>
  );
}
